import { useContext, useEffect, useState } from 'react'
import styles from './Revenue.module.css'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import {  CircularProgress, Menu, MenuItem } from '@mui/material'
import { useLazyQuery } from '@apollo/client'
import { GET_REVENUE_ADMIN, GET_REVENUE_SUPER_USER_DASHBOARD } from '../api/DashboardQuery'
import ToasterWithMessage from '../../../components/shared-components/toaster'
import { RevenueChart, formatNumberWithCommas, monthNames, oneMonthAgo, roles, roundToFixed, yearEnd, yearStart } from '../../../helper'
import { Controller, useForm } from 'react-hook-form'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { globalContext } from '../../../Provider/AppProvider'

interface RevenueResponse {
    monthsName: string
    revenue: number
    totalTickets: number
}

const Revenue = () => {
    const {error} = ToasterWithMessage()
    const [revenue,setRevenue] = useState<any>([])
    const [revenueTotal,setRevenueTotal] = useState<any>({})
    const [revenueChartresponse, setRevenueChartResponse] = useState<RevenueChart[]>()
    const [selectedStartDate,setSelectedStartDate] = useState(yearStart());
    const [selectedEndDate,setSelectedEndDate] = useState(yearEnd());
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
    const [chartWidth,setChartWidth]=useState<number>();
    const {globalState} = useContext<any>(globalContext);
  const defaultValues: any = {
    fromDate: selectedStartDate,
    toDate: selectedEndDate,
  };

  const { control, setValue,register,reset,getValues } = useForm<any>({
    defaultValues,
    shouldUnregister: false,
  });
    const [getRevenueSuperUserDetails,{loading:loadingSuperUser}] = useLazyQuery(GET_REVENUE_SUPER_USER_DASHBOARD,{
      fetchPolicy : "no-cache",
      onCompleted : (revenueData)=>{
        console.log("Response of superUserData:",revenueData);
        
        setRevenue(revenueData?.getRevenuesSuperUserDashboard?.getRevenueForSuperUser);
        setRevenueTotal(revenueData?.getRevenuesSuperUserDashboard?.getRevenueForSuperUserAggregate);
      },
      onError : (err) =>{
        error("We’re having trouble connecting. Please clear your cache, refresh the page, and try again.")
      }
    })

    const [getRevenueAdminDetails,{loading:loadingAdmin}] = useLazyQuery(GET_REVENUE_ADMIN,{
      fetchPolicy : "no-cache",
      onCompleted : (revenueData)=>{
        setRevenue(revenueData?.getRevenueForAdmin);
        setRevenueTotal(revenueData?.getRevenueForAdminAggregate);  
      },
      onError : (err) =>{
        error("We’re having trouble connecting. Please clear your cache, refresh the page, and try again.")
      }
    })
    useEffect(()=>{
       register('fromDate');
       register('toDate');
       const handleResize = () => {
        setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    },[]);

    useEffect(()=>{
      if(getValues('fromDate'),getValues('toDate')){
        const startMonth = monthNames[getValues('fromDate').getMonth()]; 
        const startYear = getValues('fromDate').getFullYear();
        const endMonth = monthNames[getValues('toDate').getMonth()]; 
        const endYear = getValues('toDate').getFullYear();
        if(globalState.profile.userRole === roles.SUPER_USER){
          getRevenueSuperUserDetails({
          variables:{
            startYear : startYear,
            endYear : endYear,
            startMonth : startMonth,
            endMonth : endMonth
          }
        })
        }else{
        getRevenueAdminDetails({
          variables:{
            startYear : startYear,
            endYear : endYear,
            startMonth : startMonth,
            endMonth : endMonth
          }
        })}
      }
    },[getValues('fromDate'),getValues('toDate')]);

     useEffect(()=>{
      if(revenue){
      setRevenueChartResponse(GenerateAreaChartData(revenue))
      }
     },[revenue])

      const customStyles = () => ({
        width: '130px',
        fontFamily:'Glegoo',
        borderRadius:'none',
        '& .MuiInputBase-input': {
          height: '5px', 
          borderRadius:'none',
          fontFamily:'Glegoo',
          color:'#5D75F8',
        },
        '& .MuiIconButton-root': {
          color:'#5d75f8'
        },
        '& .MuiDateCalendar-root':{
          overflowY: 'auto', 
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderRadius: '0', 
          borderColor:'#e3e5e8',
        },
        '& input': {
          fontFamily:'Glegoo',
          fontSize: '14px', 
        },
        '& .MuiDatePickerViewToolbar-root': {
          maxHeight: '200px', 
        },
        '& .MuiDatePickerYearSelection-container': {
          height: '20px', 
        }
      });

      const dateValidationForFromDate = (e:any, type:string) => {
        if(e === null || e === undefined || e.toString() === 'Invalid Date') {
          setSelectedStartDate(yearStart()); 
          setValue('fromDate',yearStart());
          setValue("toDate",yearEnd());
          setSelectedEndDate(yearEnd()); 
        }else{
        if (e.getTime() < selectedEndDate.getTime()) {
          if(selectedEndDate.getTime()-e.getTime() < 7776000000){
            error("Minimum 3 months interval required!");
            setValue("fromDate",yearStart());
            setSelectedStartDate(yearStart());
            setValue("toDate",yearEnd());
            setSelectedEndDate(yearEnd()); 
          }else{
          setSelectedStartDate(e);
          setValue("fromDate", e);
          }
        }else if(e.getTime()>=selectedEndDate.getTime()){
          error("From date must be less than to date")
            setValue("fromDate",yearStart());
            setSelectedStartDate(yearStart()); 
            setValue("toDate",yearEnd());
            setSelectedEndDate(yearEnd()); 
          }else{
            setValue("fromDate",yearStart());
            setSelectedStartDate(yearStart());
            setValue("toDate",yearEnd());
            setSelectedEndDate(yearEnd()); 
            } 
          }
          return;
        }

      const dateValidationForToDate = (e:any, type:string) => {
        if(e === null || e === undefined || e.toString() === 'Invalid Date') {
          setValue("fromDate",yearStart());
          setSelectedStartDate(yearStart()); 
          setSelectedEndDate(yearEnd());
          setValue('toDate',yearEnd());
        }else{
        if (selectedStartDate.getTime() < e.getTime()) {
          if(e.getTime()-selectedStartDate.getTime() < 7776000000){
            error("Minimum 3 months interval required!");
            setValue("fromDate",yearStart());
            setSelectedStartDate(yearStart()); 
            setValue("toDate",yearEnd());
            setSelectedEndDate(yearEnd());
          }else{
          setSelectedEndDate(e);
          setValue("toDate", e);
          }
        }else if(selectedStartDate.getTime()>=e.getTime()){
            error("From date must be less than to date")
            setValue("fromDate",yearStart());
            setSelectedStartDate(yearStart()); 
            setValue("toDate",yearEnd());
            setSelectedEndDate(yearEnd()); 
          }else{
            setValue("fromDate",yearStart());
            setSelectedStartDate(yearStart()); 
            setValue("toDate",yearEnd());
            setSelectedEndDate(yearEnd());
            } 
          }
          return;
        }
    const GenerateAreaChartData = (revenue : RevenueResponse[]): RevenueChart[] => {
        const monthNames : string[] = revenue.map(item => item?.monthsName)
        const uvValues : number[] = revenue.map(item => item?.revenue)
        const ticketsCount : number[] = revenue.map(item => item?.totalTickets)
        const revenueAmouts : number[] = revenue.map(item => item?.revenue)
        return monthNames.map((name,index)=> ({
            name: name,
            uv: uvValues[index],
            ticketCount: ticketsCount[index],
            revenueAmount: revenueAmouts[index]
        }))
    }
    const CustomYAxisTick = (props: any) => {
        const { x, y, payload } = props;
        const value = payload?.value >= 1000 ? `${payload?.value / 1000}k` : payload?.value; // Convert to 'k' format if >= 1000
        return <text x={x} y={y} dy={3} textAnchor="end" fill="#B9BDC6">{value}</text>;
      };
      const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload?.length) {
          const dataItem = payload[0]?.payload;
          return (
            <div className={styles.toolTipContainer}>
              <div className={styles.toolTipName}>{`Tickets: ${dataItem.ticketCount}`}</div>
              <div className={styles.toolTipDivider} />
              <div
                className={styles.toolTipValue}
              >
                {`$${dataItem.revenueAmount.toLocaleString('en-US')}`}
              </div>
            </div>
          );
        }
    
        return null;
      };

      //adjusting width of the chart
      useEffect(() => {
        if (viewportWidth > 0) {
            const maxChartWidthInVw = 88;
            const chartWidthInPx = (viewportWidth * maxChartWidthInVw) / 100;
            const dataLength = revenueChartresponse?.length ?? 1;
            const minWidthPerDataPoint = 145;
            const chartWidth = Math.max(dataLength * minWidthPerDataPoint, chartWidthInPx);

            setChartWidth(chartWidth);
        }
    }, [viewportWidth, revenueChartresponse]);

  return (
    <div className={styles.revenueMainContainer} >
          <div className={styles.revenueHeader}>
            <div className={styles.revenue}>Revenue</div>
            <div className={styles.checkboxActive}>
            <div className={styles.Fromcalender}>
            <div className={styles.sideHeading}>
                <span className={styles.FromLabelStyle}>{`From`}</span>
              </div>
            <div>
              <Controller
                name="fromDate"
                control={control}
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      {...field}
                      sx={customStyles()}
                      onChange={(e) => {
                        dateValidationForFromDate(e, "fromDate");
                      }}
                      value={selectedStartDate}
                      format='MMM yyyy'
                      slotProps={{
                        layout: {
                          sx: {
                            height: '280px',
                          }
                        }
                      }}
                      onError={() => {
                        setValue("fromDate",yearStart());
                        setSelectedStartDate(yearStart());
                        setValue("toDate",yearEnd());
                        setSelectedEndDate(yearEnd());
                      }}
                      views={['month', 'year']}
                      closeOnSelect={true}
                      minDate={new Date(2023, 0)}
                      maxDate={new Date(new Date().getFullYear() + 4, 11)}
                    />
                  </LocalizationProvider>
                )}
              />
            </div>
          </div>
          <div className={styles.Fromcalender}>
            <div className={styles.sideHeading}>
              <span className={styles.FromLabelStyle}>{`To`}</span>
            </div>
            <div>
              <Controller
                name="toDate"
                control={control}
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      {...field}
                      sx={customStyles()}
                      onChange={(e) => {
                        dateValidationForToDate(e, "toDate");
                      }}
                      slotProps={{
                        layout: {
                          sx: {
                            maxHeight: '280px'
                          }
                        }
                      }}
                      value={selectedEndDate}
                      format='MMM yyyy'
                      onError={() => {
                        setValue("fromDate",yearStart());
                        setSelectedStartDate(yearStart());
                        setValue("toDate",yearEnd());
                        setSelectedEndDate(yearEnd());
                      }}
                      views={['month', 'year']}
                      minDate={new Date(2023, 0)}
                      maxDate={new Date(new Date().getFullYear() + 4, 11)}
                    />
                  </LocalizationProvider>
                )}
              />
            </div>
              </div>
              <div className={styles.Fromcalender}>
            <div className={styles.TotalLabel}>Tickets:</div>
            <div className={styles.totalSales}>{formatNumberWithCommas(revenueTotal?.[0]?.overAllTickets ?? 0)}</div>
            </div>
            <div className={styles.Fromcalender}>
              <div className={styles.TotalLabel}>Average Ticket Price:</div>
              <div className={styles.totalSales}>${formatNumberWithCommas(revenueTotal?.[0]?.avgListPrice ?? 0)}</div>
            </div>
            <div className={styles.Fromcalender}>
              <div className={styles.TotalLabel}>Total Sales:</div>
              <div className={styles.totalSales}>${formatNumberWithCommas(revenueTotal?.[0]?.overAllSales ?? 0)}</div>
            </div>
            <div className={styles.Fromcalender}>
              <div className={styles.TotalLabel}>{globalState.profile.userRole !== roles.SUPER_USER ? "QuickAsyst Fee:" : "Lifetime Payouts :"}</div>
              <div className={styles.totalSales}>${formatNumberWithCommas(revenueTotal?.[0]?.quickasystFee ?? 0)}</div>
            </div>
            </div>
          </div>
          <div className={styles.parentContainerRevenue}>
          <div style={{display: "inline-flex", flexWrap: 'wrap'}} className={styles.revenueAreaChartContainer}>
             {
               !(loadingSuperUser || loadingAdmin)
            ?
            // <ResponsiveContainer width={'100%'} height={'100%'}>
            <AreaChart
            width={chartWidth}
            height={240}
            data={revenueChartresponse}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="10%" stopColor="#DFF2F2" stopOpacity={1} />
                <stop offset="100%" stopColor="#DFF2F2" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="name"
              axisLine={false}
              padding={{ left: 30, right:30 }}
              tickLine={{strokeOpacity: 0}}
              tick={{dy: 10.5}}
              stroke="#B9BDC6"
              className={styles.XAxisStyle}
            />
            <YAxis axisLine={false} tickLine={{ strokeOpacity: 0}} tick={<CustomYAxisTick/>} allowDecimals={false} className={styles.YAxisStyle}/>
            <CartesianGrid
              vertical={false}
              horizontal={true}
              stroke="#F3F4F6"
              strokeDasharray="4"
            />
            <Tooltip 
               cursor={{ strokeDasharray: 4, stroke:'#f3f4f6'}}
               content={<CustomTooltip/>}
            />
            <Area
              type="monotone"
              dataKey="uv"
              stroke="#10A19D"
              fillOpacity={1}
              fill="url(#colorPv)"
              strokeWidth={2}
              activeDot={{r: 5}}
            />
          </AreaChart>
          // </ResponsiveContainer>
            : 
              <div className={styles.revenueCircularProgress}>
              <CircularProgress color="primary" size={30} />
              </div>
            }
          </div>
          </div>
    </div>
  )
}

export default Revenue
